import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { ShortDatedRulesCard, suggestDiscount, shortDatedLabel } from '../components/ShortDatedRules';
import { 
  AlertTriangle, Calendar, Percent, Package, Scan, Plus, Trash2, 
  Edit, Star, Clock, Search, Loader2, Tag, FileText, DollarSign, TrendingDown, Printer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ShortDatedStock = () => {
  const { getAuthHeader } = useAuth();
  const { selectedStore } = useStore();
  
  const [shortDatedItems, setShortDatedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  
  // Add dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newItem, setNewItem] = useState({
    quantity: 1,
    expiry_date: '',
    discount_percent: 20
  });
  const [adding, setAdding] = useState(false);
  const [rules, setRules] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [discountTouched, setDiscountTouched] = useState(false);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Monthly report state
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const barcodeInputRef = useRef(null);

  const fetchMonthlyReport = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API_URL}/short-dated-stock/monthly-report`, {
        headers: getAuthHeader()
      });
      setMonthlyReport(response.data);
    } catch (error) {
      toast.error('Failed to load monthly report');
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchShortDatedStock = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/short-dated-stock?store=${selectedStore}`, {
        headers: getAuthHeader()
      });
      setShortDatedItems(response.data);
    } catch (error) {
      toast.error('Failed to load short-dated stock');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedStore]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchShortDatedStock();
    fetchProducts();
    axios.get(`${API_URL}/short-dated-stock/rules`, { headers: getAuthHeader() })
      .then(res => setRules(res.data.tiers))
      .catch(() => setRules([{ max_months: 0, discount: 30 }, { max_months: 1, discount: 20 }]));
  }, [fetchShortDatedStock, fetchProducts, getAuthHeader]);

  // Discount follows the expiry date automatically unless staff picked one by hand
  const setExpiry = (expiry_date) => {
    const auto = discountTouched ? newItem.discount_percent : suggestDiscount(expiry_date, rules);
    setNewItem({ ...newItem, expiry_date, discount_percent: auto });
  };

  const printLabelsFor = async (item, copies) => {
    const n = parseInt(copies, 10);
    if (!n || n < 1) return;
    try {
      const { printBatchLabels } = await import('../services/PrinterService');
      await printBatchLabels([shortDatedLabel(item, n)]);
      if (item.relabel_pending && item.id) {
        await axios.post(`${API_URL}/short-dated-stock/relabel-done`, { ids: [item.id] }, { headers: getAuthHeader() });
        fetchShortDatedStock();
      }
    } catch (e) {
      toast.error('Label print failed');
    }
  };

  const relabelItems = shortDatedItems.filter(i => i.relabel_pending > 0);
  const relabelCount = relabelItems.reduce((s, i) => s + i.relabel_pending, 0);

  const printAllRelabels = async () => {
    if (!relabelItems.length) return;
    try {
      const { printBatchLabels } = await import('../services/PrinterService');
      await printBatchLabels(relabelItems.map(i => shortDatedLabel(i, i.relabel_pending)));
      await axios.post(`${API_URL}/short-dated-stock/relabel-done`, { ids: relabelItems.map(i => i.id) }, { headers: getAuthHeader() });
      toast.success(`Printed ${relabelCount} fresh label${relabelCount === 1 ? '' : 's'}`);
      fetchShortDatedStock();
    } catch (e) {
      toast.error('Label print failed — labels still queued');
    }
  };

  const [runningMarkdown, setRunningMarkdown] = useState(false);
  const runMarkdownNow = async () => {
    if (!window.confirm('Move every short-dated item up to the discount its expiry month now calls for (e.g. last month\'s 20% items → 30%)?')) return;
    setRunningMarkdown(true);
    try {
      const res = await axios.post(`${API_URL}/short-dated-stock/run-markdown`, {}, { headers: getAuthHeader() });
      toast.success(res.data.updated ? `${res.data.updated} item${res.data.updated === 1 ? '' : 's'} marked down — fresh labels queued` : 'Nothing to mark down — all discounts already match the rules');
      fetchShortDatedStock();
    } catch (e) {
      toast.error('Markdown failed');
    } finally {
      setRunningMarkdown(false);
    }
  };

  const promptPrintLabels = (item) => {
    const copies = window.prompt(`How many short-dated labels for ${item.product_name}?`, String(item.quantity || 1));
    if (copies === null) return;
    printLabelsFor(item, copies);
  };

  const applySuggestedDiscount = async (item) => {
    try {
      await axios.put(`${API_URL}/short-dated-stock/${item.id}`, { discount_percent: item.suggested_discount }, { headers: getAuthHeader() });
      toast.success(`${item.product_name} now ${item.suggested_discount}% OFF — reprint its labels`);
      fetchShortDatedStock();
    } catch (e) {
      toast.error('Could not update discount');
    }
  };

  // Handle barcode scan
  const handleBarcodeScan = async (barcode) => {
    if (!barcode.trim()) return;
    
    setScanning(true);
    try {
      const response = await axios.get(`${API_URL}/short-dated-stock/by-barcode/${barcode}?store=${selectedStore}`, {
        headers: getAuthHeader()
      });
      
      if (response.data.product) {
        setSelectedProduct(response.data.product);
        if (response.data.has_short_dated) {
          toast.info(`This product already has ${response.data.short_dated.quantity} short-dated units`);
        }
        setBarcodeInput('');
      } else {
        toast.error('Product not found');
      }
    } catch (error) {
      toast.error('Failed to lookup product');
    } finally {
      setScanning(false);
    }
  };

  // Handle product search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const results = products.filter(p => 
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.barcode?.includes(query) ||
        p.sku?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10);
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  // Add short-dated stock
  const handleAddShortDated = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    if (!newItem.expiry_date) {
      toast.error('Please enter an expiry date');
      return;
    }
    
    setAdding(true);
    try {
      const res = await axios.post(`${API_URL}/short-dated-stock`, {
        product_id: selectedProduct.id,
        store: selectedStore,
        quantity: newItem.quantity,
        expiry_date: newItem.expiry_date,
        discount_percent: newItem.discount_percent
      }, { headers: getAuthHeader() });
      
      const added = {
        product_name: selectedProduct.name,
        product_barcode: selectedProduct.barcode,
        original_price: selectedProduct.sell_price,
        discounted_price: Math.round(selectedProduct.sell_price * (1 - newItem.discount_percent / 100) * 100) / 100,
        discount_percent: newItem.discount_percent,
        expiry_date: newItem.expiry_date,
        quantity: newItem.quantity,
        id: res.data.id,
      };
      toast.success(`Added ${newItem.quantity}x ${selectedProduct.name} at ${newItem.discount_percent}% OFF`, {
        duration: 12000,
        action: { label: `Print ${newItem.quantity} label${newItem.quantity > 1 ? 's' : ''}`, onClick: () => printLabelsFor(added, newItem.quantity) },
      });
      setShowAddDialog(false);
      setSelectedProduct(null);
      setNewItem({ quantity: 1, expiry_date: '', discount_percent: 20 });
      setDiscountTouched(false);
      fetchShortDatedStock();
    } catch (error) {
      toast.error('Failed to add short-dated stock');
    } finally {
      setAdding(false);
    }
  };

  // Edit short-dated item
  const handleEdit = (item) => {
    setEditingItem(item);
    setEditData({
      quantity: item.quantity,
      expiry_date: item.expiry_date.split('T')[0],
      discount_percent: item.discount_percent
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/short-dated-stock/${editingItem.id}`, editData, {
        headers: getAuthHeader()
      });
      toast.success('Short-dated item updated');
      setShowEditDialog(false);
      fetchShortDatedStock();
    } catch (error) {
      toast.error('Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  // Delete short-dated item
  const handleDelete = async (item) => {
    if (!window.confirm(`Remove ${item.product_name} from short-dated stock?`)) return;
    
    try {
      await axios.delete(`${API_URL}/short-dated-stock/${item.id}`, { headers: getAuthHeader() });
      toast.success('Item removed from short-dated stock');
      fetchShortDatedStock();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Pending EXPIRED label queue — stored in localStorage so labels accumulate
  // across clicks (and survive a page reload). Staff press "Print pending labels"
  // when the queue has at least 3 (the label sheet is 3-across) so no label is wasted.
  const PENDING_KEY = 'expired_labels_pending';
  const loadPending = () => {
    try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]'); } catch { return []; }
  };
  const [pendingLabels, setPendingLabels] = useState(loadPending());
  const savePending = (arr) => {
    try { localStorage.setItem(PENDING_KEY, JSON.stringify(arr)); } catch {}
    setPendingLabels(arr);
  };

  // Move a short-dated item to the EXPIRED clearance bucket. Stock leaves the
  // original product (so reports / margins stay clean) and lands in a single
  // EXPIRED-<STORE> SKU that can be sold at any price the staff types.
  // Labels go INTO A QUEUE (not printed immediately) — staff prints in 3-up batches.
  const handleMarkExpired = async (item) => {
    const ok = window.confirm(
      `Move ${item.quantity} × ${item.product_name} to Expired Clearance?\n\n` +
      `• Stock leaves the original product (no margin/inventory pollution)\n` +
      `• Goes into the EXPIRED clearance bucket\n` +
      `• Adds ${item.quantity} EXPIRED label(s) to the print queue (waits until you have 3+ so no label is wasted)\n\n` +
      'Continue?'
    );
    if (!ok) return;
    try {
      const res = await axios.post(`${API_URL}/short-dated-stock/${item.id}/mark-expired`, {}, { headers: getAuthHeader() });
      // Queue the labels — one entry per physical bottle
      const newLabel = {
        name: `EXPIRED — ${item.product_name}`.slice(0, 60),
        barcode: res.data.barcode_to_label,
        sell_price: res.data.expired_product?.sell_price || 0,
      };
      const next = [...loadPending()];
      for (let i = 0; i < res.data.quantity_moved; i++) next.push(newLabel);
      savePending(next);
      toast.success(`${res.data.message}. ${next.length} label${next.length === 1 ? '' : 's'} in queue.`);
      fetchShortDatedStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to Expired');
    }
  };

  // Flush the queue → opens browser print dialog with all queued labels arranged
  // 3-across on the label sheet. After printing, clears the queue.
  const printPendingExpiredLabels = async () => {
    const pending = loadPending();
    if (pending.length === 0) return toast.info('No labels queued');
    if (pending.length % 3 !== 0) {
      const ok = window.confirm(
        `You have ${pending.length} label${pending.length === 1 ? '' : 's'} queued — not a multiple of 3 (your label sheet is 3-across).\n\n` +
        `Printing now will waste ${3 - (pending.length % 3)} label${pending.length % 3 === 2 ? '' : 's'} on the last row.\n\n` +
        'Print anyway?'
      );
      if (!ok) return;
    }
    try {
      const { printBatchLabels } = await import('../services/PrinterService');
      // Pass with copies=1 each; printBatchLabels expands and arranges 3-up itself.
      await printBatchLabels(pending.map(p => ({ ...p, copies: 1 })));
      savePending([]);
      toast.success(`Printed ${pending.length} EXPIRED label${pending.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error('Label print failed — labels still queued, try again.');
    }
  };

  const clearPendingExpiredLabels = () => {
    if (!window.confirm(`Discard ${loadPending().length} queued EXPIRED labels?`)) return;
    savePending([]);
    toast.success('Queue cleared');
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get expiry badge color
  const getExpiryBadge = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days <= 0) return { color: 'bg-red-500', text: 'EXPIRED' };
    if (days <= 30) return { color: 'bg-red-500', text: `${days} days` };
    if (days <= 60) return { color: 'bg-orange-500', text: `${days} days` };
    if (days <= 90) return { color: 'bg-yellow-500', text: `${days} days` };
    return { color: 'bg-emerald-500', text: `${days} days` };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="short-dated-stock-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" />
            Short-Dated Stock
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track and sell products expiring within 5 months at discounted prices
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowRules(v => !v)}
            data-testid="toggle-sd-rules"
          >
            <Percent className="w-4 h-4 mr-2" /> Discount rules
          </Button>
          <Button 
            onClick={() => { setDiscountTouched(false); setShowAddDialog(true); }}
            className="bg-yellow-500 hover:bg-yellow-600"
            data-testid="add-short-dated-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Short-Dated Item
          </Button>
        </div>
      </div>

      {showRules && rules && (
        <div className="space-y-3">
          <ShortDatedRulesCard tiers={rules} onSaved={(t) => { setRules(t); fetchShortDatedStock(); }} />
          <div className="flex items-center justify-between gap-3 rounded-md border p-3 bg-slate-50 text-sm" data-testid="sd-markdown-info">
            <p className="text-slate-600">
              <span className="font-semibold text-slate-800">Automatic monthly markdown:</span> on the 1st of each month every item is moved up to the discount its expiry month now calls for, and fresh labels are queued here.
            </p>
            <Button size="sm" variant="outline" onClick={runMarkdownNow} disabled={runningMarkdown} data-testid="sd-run-markdown">
              {runningMarkdown ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingDown className="w-4 h-4 mr-1" />} Run markdown now
            </Button>
          </div>
        </div>
      )}

      {relabelItems.length > 0 && (
        <div className="rounded-md border-2 border-orange-300 bg-orange-50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="sd-relabel-banner">
          <div>
            <p className="font-bold text-orange-900">📉 {relabelItems.length} item{relabelItems.length === 1 ? '' : 's'} marked down — {relabelCount} fresh label{relabelCount === 1 ? '' : 's'} to print</p>
            <p className="text-sm text-orange-800">
              {relabelItems.slice(0, 4).map(i => `${i.product_name} (${i.previous_discount || '?'}% → ${i.discount_percent}%)`).join(', ')}{relabelItems.length > 4 ? ` +${relabelItems.length - 4} more` : ''}
            </p>
          </div>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={printAllRelabels} data-testid="sd-print-relabels">
            <Printer className="w-4 h-4 mr-1" /> Print {relabelCount} label{relabelCount === 1 ? '' : 's'}
          </Button>
        </div>
      )}

      {/* EXPIRED-label print queue banner — shows when there are queued labels
          so staff don't waste label stock by printing one at a time. */}
      {pendingLabels.length > 0 && (
        <div className={`rounded-md border-2 p-3 flex items-center justify-between gap-3 flex-wrap ${pendingLabels.length >= 3 ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="expired-label-queue">
          <div>
            <p className={`font-bold ${pendingLabels.length >= 3 ? 'text-emerald-900' : 'text-amber-900'}`}>
              📦 {pendingLabels.length} EXPIRED label{pendingLabels.length === 1 ? '' : 's'} queued
            </p>
            <p className={`text-sm ${pendingLabels.length >= 3 ? 'text-emerald-800' : 'text-amber-800'}`}>
              {pendingLabels.length >= 3
                ? `Ready to print — full rows of 3. ${pendingLabels.length % 3 === 0 ? 'No waste.' : `(${pendingLabels.length % 3} label${pendingLabels.length % 3 === 1 ? '' : 's'} will be on a partial row — ${3 - (pendingLabels.length % 3)} waste.)`}`
                : `Add ${3 - pendingLabels.length} more to fill a row of 3 (your label sheet is 3-across).`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearPendingExpiredLabels}
              data-testid="clear-expired-queue"
            >
              Clear queue
            </Button>
            <Button
              size="sm"
              className={pendingLabels.length >= 3 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'}
              onClick={printPendingExpiredLabels}
              data-testid="print-pending-expired"
            >
              🖨️ Print {pendingLabels.length} label{pendingLabels.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Items</p>
                <p className="text-2xl font-bold">{shortDatedItems.length}</p>
              </div>
              <Package className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Units</p>
                <p className="text-2xl font-bold">
                  {shortDatedItems.reduce((sum, item) => sum + item.quantity, 0)}
                </p>
              </div>
              <Tag className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expiring in 30 Days</p>
                <p className="text-2xl font-bold text-red-500">
                  {shortDatedItems.filter(item => getDaysUntilExpiry(item.expiry_date) <= 30).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setShowReport(!showReport); if (!monthlyReport) fetchMonthlyReport(); }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Monthly Loss Report</p>
                <p className="text-2xl font-bold text-red-600">
                  <TrendingDown className="w-5 h-5 inline mr-1" />
                  View
                </p>
              </div>
              <FileText className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Loss Report */}
      {showReport && (
        <Card className="border-2 border-red-200">
          <CardHeader className="bg-red-50 border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <TrendingDown className="w-5 h-5" />
                  Monthly Wholesale Loss Report
                </CardTitle>
                <CardDescription>How much short-dated stock costs you each month (at wholesale/buy price)</CardDescription>
              </div>
              {monthlyReport && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Wholesale Loss</p>
                  <p className="text-2xl font-bold text-red-600">${monthlyReport.totals.total_wholesale_value.toLocaleString()}</p>
                  <p className="text-xs text-slate-400">Retail value: ${monthlyReport.totals.total_retail_value.toLocaleString()}</p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingReport ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-red-400" />
              </div>
            ) : monthlyReport && monthlyReport.months.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-bold">Month</TableHead>
                    <TableHead className="text-center font-bold">Items</TableHead>
                    <TableHead className="text-center font-bold">Units</TableHead>
                    <TableHead className="text-right font-bold">Wholesale Value</TableHead>
                    <TableHead className="text-right font-bold">Retail Value</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyReport.months.map((month) => {
                    const monthDate = new Date(month.month + '-01');
                    const monthName = monthDate.toLocaleDateString('en-NZ', { year: 'numeric', month: 'long' });
                    const isExpanded = expandedMonth === month.month;
                    const isPast = new Date(month.month + '-28') < new Date();
                    return (
                      <React.Fragment key={month.month}>
                        <TableRow
                          className={`cursor-pointer hover:bg-red-50 ${isPast ? 'bg-red-25' : ''}`}
                          onClick={() => setExpandedMonth(isExpanded ? null : month.month)}
                          data-testid={`month-row-${month.month}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-red-400" />
                              {monthName}
                              {isPast && <Badge className="bg-red-100 text-red-600 text-xs">Expired</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{month.items_count}</TableCell>
                          <TableCell className="text-center">{month.total_units}</TableCell>
                          <TableCell className="text-right font-bold text-red-600">${month.wholesale_value.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-slate-500">${month.retail_value.toLocaleString()}</TableCell>
                          <TableCell className="text-center text-slate-400">{isExpanded ? '-' : '+'}</TableCell>
                        </TableRow>
                        {isExpanded && month.products.map((product, idx) => (
                          <TableRow key={`${month.month}-${idx}`} className="bg-slate-50">
                            <TableCell className="pl-10 text-sm">{product.name}</TableCell>
                            <TableCell className="text-center text-sm">{product.quantity}</TableCell>
                            <TableCell className="text-center text-sm">${product.buy_price.toFixed(2)} ea</TableCell>
                            <TableCell className="text-right text-sm font-medium text-red-500">${product.wholesale_total.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-xs text-slate-400">{formatDate(product.expiry_date)}</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No short-dated stock data to report</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Short-Dated Items Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-500" />
            Short-Dated Items - {selectedStore?.charAt(0).toUpperCase() + selectedStore?.slice(1)}
          </CardTitle>
          <CardDescription>
            Products marked with expiry dates. Sold at discounted prices at POS.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {shortDatedItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No short-dated items for this store</p>
              <p className="text-sm">Add items using the button above</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead className="text-center">Expiry</TableHead>
                  <TableHead className="text-center">Discount</TableHead>
                  <TableHead className="text-right">Original</TableHead>
                  <TableHead className="text-right">Sale Price</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shortDatedItems.map((item) => {
                  const expiryBadge = getExpiryBadge(item.expiry_date);
                  return (
                    <TableRow key={item.id} data-testid={`short-dated-row-${item.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{item.product_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.product_barcode || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{item.quantity}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${expiryBadge.color} text-white`}>
                          {expiryBadge.text}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(item.expiry_date)}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-yellow-500 text-white">
                          {item.discount_percent}% OFF
                        </Badge>
                        {item.relabel_pending > 0 && (
                          <p className="text-xs text-orange-600 mt-1" data-testid={`sd-relabel-flag-${item.id}`}>new label needed</p>
                        )}
                        {item.suggested_discount > item.discount_percent && (
                          <button
                            type="button"
                            onClick={() => applySuggestedDiscount(item)}
                            className="block mx-auto mt-1 text-xs text-orange-600 underline"
                            title="Expiry is closer now — apply the rule discount"
                            data-testid={`sd-apply-suggested-${item.id}`}
                          >
                            rule says {item.suggested_discount}% — apply
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-right line-through text-slate-400">
                        ${item.original_price.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        ${item.discounted_price.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => promptPrintLabels(item)}
                            title="Print short-dated price labels"
                            data-testid={`sd-print-label-${item.id}`}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm"
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => handleMarkExpired(item)}
                            title="Move to EXPIRED clearance — prints relabel barcode"
                            data-testid={`mark-expired-${item.id}`}
                          >
                            <AlertTriangle className="w-4 h-4 mr-1" /> Expired
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDelete(item)}
                            title="Remove from short-dated (no stock movement)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <Star className="w-5 h-5" />
              Add Short-Dated Stock
            </DialogTitle>
            <DialogDescription>
              Scan or search for a product to add to short-dated inventory
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Barcode Scanner */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scan className="w-4 h-4" /> Scan Barcode
              </Label>
              <div className="flex gap-2">
                <Input
                  ref={barcodeInputRef}
                  placeholder="Scan or enter barcode..."
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBarcodeScan(barcodeInput)}
                  autoFocus
                />
                <Button 
                  onClick={() => handleBarcodeScan(barcodeInput)}
                  disabled={scanning}
                >
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
                </Button>
              </div>
            </div>

            {/* Product Search */}
            <div className="space-y-2 relative">
              <Label className="flex items-center gap-2">
                <Search className="w-4 h-4" /> Or Search Product
              </Label>
              <Input
                placeholder="Search by name, SKU..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="p-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                      onClick={() => selectProduct(product)}
                    >
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.barcode || product.sku}</p>
                      </div>
                      <span className="text-sm text-emerald-600">${product.sell_price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Product */}
            {selectedProduct && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold">{selectedProduct.name}</p>
                      <p className="text-sm text-slate-500">
                        Barcode: {selectedProduct.barcode || 'N/A'} | 
                        Price: ${selectedProduct.sell_price?.toFixed(2)}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedProduct(null)}
                    >
                      Change
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quantity, Expiry, Discount */}
            {selectedProduct && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={newItem.expiry_date}
                    onChange={(e) => setExpiry(e.target.value)}
                    data-testid="sd-add-expiry"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount % {!discountTouched && newItem.expiry_date && <span className="text-xs text-emerald-600">(auto)</span>}</Label>
                  <Select
                    value={String(newItem.discount_percent)}
                    onValueChange={(val) => { setDiscountTouched(true); setNewItem({...newItem, discount_percent: parseInt(val)}); }}
                  >
                    <SelectTrigger data-testid="sd-add-discount">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set([10, 20, 30, ...(rules || []).map(t => t.discount), newItem.discount_percent])].sort((a, b) => a - b).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}% OFF</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Preview */}
            {selectedProduct && newItem.expiry_date && (
              <div className="bg-slate-100 p-3 rounded-lg" data-testid="sd-add-preview">
                <p className="text-sm text-slate-600">
                  Sale Price Preview — expires in {getDaysUntilExpiry(newItem.expiry_date)} days ({new Date(newItem.expiry_date).toLocaleString('en-NZ', { month: 'short', year: 'numeric' })})
                  {!discountTouched && ' · discount set from your expiry rules'}
                </p>
                <div className="flex items-center gap-3">
                  <span className="line-through text-slate-400">${selectedProduct.sell_price?.toFixed(2)}</span>
                  <span className="text-xl font-bold text-emerald-600">
                    ${(selectedProduct.sell_price * (1 - newItem.discount_percent / 100)).toFixed(2)}
                  </span>
                  <Badge className="bg-yellow-500">{newItem.discount_percent}% OFF</Badge>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddShortDated}
              disabled={!selectedProduct || !newItem.expiry_date || adding}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              {adding ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
              ) : (
                <><Star className="w-4 h-4 mr-2" /> Add to Short-Dated</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-yellow-500" />
              Edit Short-Dated Item
            </DialogTitle>
            <DialogDescription>
              {editingItem?.product_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min="0"
                value={editData.quantity || ''}
                onChange={(e) => setEditData({...editData, quantity: parseInt(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={editData.expiry_date || ''}
                onChange={(e) => setEditData({...editData, expiry_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Discount %</Label>
              <Select
                value={String(editData.discount_percent)}
                onValueChange={(val) => setEditData({...editData, discount_percent: parseInt(val)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([10, 20, 30, ...(rules || []).map(t => t.discount), editData.discount_percent || 20])].sort((a, b) => a - b).map(d => (
                    <SelectItem key={d} value={String(d)}>{d}% OFF</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShortDatedStock;
